import { Component, Element, Prop, State, h } from '@stencil/core';

import { onVehicleUserSelected } from '../../../actions/actions.vehicle-config';
import { AnswerVehicle, VehicleOption } from '../../../interfaces/interface.vehicle';
import { VehicleConfigState } from '../../../store/store.vehicle-config';
import { groupArrayByKey } from '../../../utils/utils.js';

@Component({
  tag: 'vehicle-equipment',
  styleUrl: 'vehicle-equipment.css',
})
export class VehicleEquipment {
  @Element() el: HTMLElement;

  @Prop() vehicleConfig: VehicleConfigState;
  @Prop() vehicleAnswers: AnswerVehicle = {
    apiSelectedOptionIds: [],
    apiUnselectedOptionIds: [],
  } as AnswerVehicle;
  @Prop() vehicleTrimId: number = 0;

  @State() lastInteractedOption: VehicleOption;

  openChangedModal() {
    const modal = this.el.querySelector('changed-options-modal').querySelector('ico-modal#changed-options-modal') as HTMLIcoModalElement;
    modal.open();
  }

  getModalOptions() {
    const { availEngines, availTransmissions, availDrivetrains } = this.vehicleConfig;

    let modalOptions: VehicleOption[] = [];

    // Add engine, transmission, drive train as locked options
    if (availEngines.length === 1) {
      modalOptions.push({ ...availEngines[0], isLocked: true, isSelected: true });
    }

    if (availTransmissions.length === 1) {
      modalOptions.push({ ...availTransmissions[0], isLocked: true, isSelected: true });
    }

    if (availDrivetrains.length === 1) {
      modalOptions.push({ ...availDrivetrains[0], isLocked: true, isSelected: true });
    }

    // Add other standard options
    modalOptions = modalOptions.concat(this.vehicleConfig.availOptions.filter((opt) => opt.showInModal));

    return modalOptions;
  }

  setSelectedEquipment(option: VehicleOption, isSelected: boolean) {
    const { optionId } = option;

    this.lastInteractedOption = { ...option, isSelected };

    onVehicleUserSelected(this.vehicleTrimId, { optionId, isSelected });
  }

  getAnswerOptions(): VehicleOption[] {
    return this.vehicleConfig.availOptions.map((option) => {
      return {
        ...option,
        isSelected: this.vehicleAnswers.apiSelectedOptionIds.includes(option.optionId),
      };
    });
  }

  renderChangedBySystemIndicator(option: VehicleOption) {
    return (
      <div
        class={{
          'overflow-hidden transition-all duration-300 ': true,
          'w-0 opacity-0 scale-50': !option.wasChangedBySystem,
          'xs:w-8 sm:w-4 opacity-1 scale-100 sm:mt-1 sm:ml-0.5 sm:-mr-2 xs:px-2 sm:p-0': option.wasChangedBySystem,
        }}>
        <button
          title="This option was automatically changed based on your last selection"
          class="p-0  border-0 bg-transparent flex justify-center xs:items-center sm:items-start cursor-pointer w-full h-full"
          onClick={() => this.openChangedModal()}>
          <icon-alert class="vehicle-equipment__changed-by-system-icon w-full" />
        </button>
      </div>
    );
  }

  renderOptions() {
    const pageOptions = this.vehicleConfig.availOptions.filter((opt) => !opt.showInModal);
    const vehicleEquipData = groupArrayByKey(pageOptions, 'categoryName');
    const equipKeys = Object.keys(vehicleEquipData);

    return equipKeys.map((category) => (
      <div class="mt-5">
        <div class="vehicle-equipment__subheader">{category}</div>
        <div class="vehicle-equipment__options-container">
          {vehicleEquipData[category].map((option) => (
            <div class="flex xs:w-full sm:w-auto">
              <input-checkbox-toggle
                class="xs:flex-1 sm:flex-none"
                titleText={option.displayName}
                isChecked={this.vehicleAnswers.apiSelectedOptionIds.includes(option.optionId)}
                isDisabled={option.isLocked}
                whenChanged={(isSelected) => this.setSelectedEquipment(option, isSelected)}
              />
              {this.renderChangedBySystemIndicator(option)}
            </div>
          ))}
        </div>
      </div>
    ));
  }

  render() {
    const showStandardEquipLink = this.vehicleConfig.availOptions.filter((opt) => opt.showInModal).length > 0;

    return (
      <box-shadow>
        <div class="ico-components-heading-large mb-2">What Equipment Is on Your Vehicle?</div>
        {showStandardEquipLink && (
          <standard-equipment-modal
            options={this.getModalOptions()}
            answerOptions={this.getAnswerOptions()}
            setSelectedEquipment={(option, isSelected) => this.setSelectedEquipment(option, isSelected)}
          />
        )}
        {this.renderOptions()}
        <changed-options-modal lastInteractedOption={this.lastInteractedOption} options={this.getAnswerOptions()} />
      </box-shadow>
    );
  }
}
